<template>
  <div>
    <div class="rm-relative">
      <div class="field has-addons">
        <p class="control is-expanded">
          <input
            type="text"
            class="input"
            placeholder="Search a location or landmark"
            v-model="searchText"
            @keyup.enter="onSearchAddress"
          />
        </p>
        <p class="control">
          <button class="button" @click="loadCurrentLocation">
            <span class="icon">
              <font-awesome-icon icon="location-arrow" />
            </span>
          </button>
        </p>
        <p class="control">
          <button class="button" @click="onSearchAddress">
            <span class="icon">
              <font-awesome-icon icon="search" />
            </span>
          </button>
        </p>
      </div>
      <div v-if="hasSuggestions" class="panel rm-dropdown">
        <a
          v-for="suggestion in suggestions"
          :key="suggestion.place_id"
          href="#"
          @click.prevent="onSelectSuggestion(suggestion)"
          class="panel-block"
        >{{ suggestion.description }}</a>
      </div>
      <span class="subtitle is-7 has-text-grey">Type a recognizable street/area/locality name to enter full address</span>
    </div>
    <div>
      <div class="google-map" ref="googleMap"></div>
      <template v-if="Boolean(this.google) && Boolean(this.map)">
        <slot :google="google" :map="map" />
      </template>
      <br />
    </div>
    <p class="help is-danger" v-if="errors.location">{{errors.location.msg}}</p>
    <div v-if="addressLine || errors.addressLine" class="field is-horizontal">
      <div class="field-label is-normal">
        <label class="label has-text-weight-medium">Address Line</label>
      </div>
      <div class="field-body">
        <div class="field">
          <div class="control">
            <input
              :class="['input', errors.addressLine ? 'is-danger': '']"
              type="text"
              v-model="addressLine"
              maxlength="250"
              required
            />
          </div>
          <p class="help is-danger" v-if="errors.addressLine">{{errors.addressLine.msg}}</p>
        </div>
      </div>
    </div>
    <div v-if="addressLine" class="field is-horizontal">
      <div class="field-label"></div>
      <div class="field-body">{{addressComponents}}</div>
    </div>
  </div>
</template>

<script>
import GoogleMapsApiLoader from "google-maps-api-loader";
import Util from '@/util';

export default {
  name: "AddressPicker",
  props: {
    mapConfig: {},
    defaultAddress: null
  },

  data() {
    return {
      errors: {},
      suggestions: [],
      searchText: null,
      addressLine: null,
      city: null,
      state: null,
      zip: null,
      country: null,
      geolocation: null,
      google: null,
      map: null,
      deliverHereMarker: null
    };
  },

  watch: {
    defaultAddress() {
      if (this.defaultAddress) {
        this.addressLine = this.defaultAddress.addressLine;
        this.city = this.defaultAddress.city;
        this.state = this.defaultAddress.city;
        this.zip = this.defaultAddress.zip;
        this.country = this.defaultAddress.country;
        this.geolocation = this.defaultAddress.geolocation;
        this.setDeliverHereMarker({
          latitude: this.geolocation[1],
          longitude: this.geolocation[0]
        });
      } else {
        this.addressLine = null;
        this.city = null;
        this.state = null;
        this.zip = null;
        this.country = null;
        this.geolocation = null;
        this.setDeliverHereMarker();
      }
    },

    addressLine() {
      delete this.errors.addressLine;
      if (!this.addressLine || this.addressLine.trim().length === 0) {
        this.errors.addressLine = {
          msg: "Address line is required"
        };
      }

      if (
        this.addressLine &&
        this.addressLine.trim().length > 0 &&
        this.geolocation
      ) {
        const address = {
          addressLine: this.addressLine,
          city: this.city,
          state: this.state,
          zip: this.zip,
          country: this.country,
          geolocation: [this.geolocation[0], this.geolocation[1]]
        }
        this.$emit("addressChanged", address);
        this.$store.dispatch('user/updateLastSearchedAddr', address);
      }
    }
  },

  async mounted() {
    this.google = await GoogleMapsApiLoader({
      libraries: ["places", "geometry"],
      apiKey: "AIzaSyC3RbBRDbaV5sT4y4KLfc74-Oes5j0CfyQ"
    });

    const mapContainer = this.$refs.googleMap;
    const { geolocation = [], outletName } = this.mapConfig || {};

    this.map = new this.google.maps.Map(mapContainer, {
      center: { lat: geolocation[1] || 0, lng: geolocation[0] || 0 },
      clickableIcons: false,
      disableDefaultUI: true,
      mapTypeId: this.google.maps.MapTypeId.ROADMAP,
      zoom: 15,
      zoomControl: true
    });

    this.map.addListener("click", e => {
      const coords = {
        latitude: e.latLng.lat(),
        longitude: e.latLng.lng()
      };
      if (this.isLocationValid(coords)) {
        this.setDeliverHereMarker(coords);
        this.getAddressByLocation(coords);
      }
    });

    const marker = new this.google.maps.Marker({
      map: this.map,
      anchorPoint: new this.google.maps.Point(0, -29),
      clickable: false,
      icon: {
        url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        labelOrigin: new this.google.maps.Point(15, 40)
      },
      label: {
        fontWeight: "bold",
        text: outletName
      },
      position: {
        lat: geolocation[1],
        lng: geolocation[0]
      }
    });

    marker.setVisible(true);
  },

  computed: {
    addressComponents() {
      let out = [];
      [this.city, this.state, this.country, this.zip].forEach(field => {
        if (field) out.push(field);
      });
      return out.join(", ");
    },
    hasSuggestions() {
      if (this.suggestions && this.suggestions.length > 0) return true;
      return false;
    }
  },
  methods: {
    resetAddress() {
      this.addressLine = null;
      this.city = null;
      this.state = null;
      this.zip = null;
      this.country = null;
      this.geolocation = null;
    },

    loadCurrentLocation() {
      navigator.geolocation.getCurrentPosition(
        pos => {
          if (this.isLocationValid(pos.coords)) {
            this.setDeliverHereMarker(pos.coords);
            this.getAddressByLocation(pos.coords);
          }
        },
        () => {
          // error do nothing
        },
        { maximumAge: 3000, timeout: 30000, enableHighAccuracy: true }
      );
    },

    isLocationValid(coords) {
      const { maxDistance = 5, distanceUnit = "km", geolocation } =
        this.mapConfig || {};

      let radius = maxDistance * 1000;
      if (distanceUnit === "mi") {
        radius = maxDistance * 1609.34;
      }

      const distanceFromOutlet = this.google.maps.geometry.spherical.computeDistanceBetween(
        new this.google.maps.LatLng(geolocation[1], geolocation[0]),
        new this.google.maps.LatLng(coords.latitude, coords.longitude)
      );

      delete this.errors.location;
      if (distanceFromOutlet > radius) {
        Util.notify(this, {
          type: 'warn',
          message: 'Location unserviceable.'
        });
        this.errors.location = {
          msg: "Location unserviceable."
        };
        this.resetAddress();
        return false;
      }
      return true;
    },

    getAddressByLocation(coords) {
      var geoCoder = new this.google.maps.Geocoder();
      geoCoder.geocode(
        {
          location: {
            lat: coords.latitude,
            lng: coords.longitude
          }
        },
        (results, status) => {
          if (status === this.google.maps.GeocoderStatus.OK) {
            this.parseAddress(results);
          }
        }
      );
    },

    setDeliverHereMarker(coords) {
      if (!coords) {
        this.map.panTo({
          lat: this.mapConfig.geolocation[1],
          lng: this.mapConfig.geolocation[0]
        });

        if (this.deliverHereMarker) {
          this.deliverHereMarker.setVisible(false);
        }

        return;
      }

      this.map.panTo({
        lat: coords.latitude,
        lng: coords.longitude
      });

      this.geolocation = [coords.longitude, coords.latitude];

      if (!this.deliverHereMarker) {
        this.deliverHereMarker = new this.google.maps.Marker({
          map: this.map,
          anchorPoint: new this.google.maps.Point(0, -29),
          clickable: false,
          icon: {
            url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
            labelOrigin: new this.google.maps.Point(15, 40)
          },
          label: {
            fontWeight: "bold",
            text: "Deliver Here"
          }
        });
      }
      this.deliverHereMarker.setVisible(true);
      this.deliverHereMarker.setPosition({
        lat: coords.latitude,
        lng: coords.longitude
      });
    },

    onSearchAddress() {
      const autoComplete = new this.google.maps.places.AutocompleteService();
      const rParams = {
        input: this.searchText
      };

      const { countryCode, maxDistance = 5, distanceUnit = "km", geolocation } =
        this.mapConfig || {};

      if (countryCode) {
        rParams.componentRestrictions = {
          country: this.mapConfig.countryCode
        };
      }

      let radius = maxDistance * 1000;
      if (distanceUnit === "mi") {
        radius = maxDistance * 1609.34;
      }

      if (geolocation) {
        const coords = this.mapConfig.geolocation;
        rParams.location = new this.google.maps.LatLng(coords[1], coords[0]);
        rParams.radius = radius;
      }

      autoComplete.getPlacePredictions(rParams, results => {
        this.suggestions = results;
      });
    },

    onSelectSuggestion(suggestion) {
      this.suggestions = [];
      this.searchText = null;
      this.addressLine = suggestion.description;
      const placesService = new this.google.maps.places.PlacesService(this.map);
      placesService.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ["address_components", "formatted_address", "geometry"]
        },
        place => {
          if (place && place.geometry) {
            const coords = {
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng()
            };
            if (this.isLocationValid(coords)) {
              this.setDeliverHereMarker(coords);
              this.parseAddress([place]);
            }
          }
        }
      );
    },

    parseAddress(results) {
      const me = this;
      const mapping = {
        zip: "postal_code",
        city: "locality",
        state: "administrative_area_level_1",
        country: "country",
        countryCode: "country",
        a0: "establishment",
        a1: "room",
        a2: "post_box",
        a3: "floor",
        a4: "premise",
        a5: "subpremise",
        a6: "street_number",
        a7: "street_address",
        a8: "route",
        a9: "neighborhood",
        a10: "sublocality_level_5",
        a11: "sublocality_level_4",
        a12: "sublocality_level_3",
        a13: "sublocality_level_2",
        a14: "sublocality_level_1"
      };
      const entity = results[0];

      let tmp = {};
      let al = "";
      Object.keys(mapping).forEach(function(key) {
        let add;
        for (let i = 0, n = entity.address_components.length; i < n; i += 1) {
          add = entity.address_components[i] || {};
          add.types = add.types || [];
          if (add.types.indexOf(mapping[key]) > -1) {
            if (key.charAt(0) === "a") {
              tmp[key] = add.long_name;
            } else if (key === "countryCode") {
              me[key] = add.short_name;
            } else {
              me[key] = add.long_name;
            }
            break;
          }
        }
      });

      for (let i = 0, n = 14; i <= n; i += 1) {
        if (tmp["a" + i]) {
          al = al + tmp["a" + i] + ", ";
        }
      }

      this.addressLine = al.substr(0, al.length - 2);

      var start = this.addressLine.substr(0, 5);
      if (entity.formatted_address.indexOf(start) > 0) {
        this.addressLine =
          entity.formatted_address.substr(
            0,
            entity.formatted_address.indexOf(start)
          ) + this.addressLine;
      }
    }
  }
};
</script>
<style scoped>
.google-map {
  width: 100%;
  height: 300px;
}
</style>
